// THE WHATSAPP FUNNEL (owner, 2026-10-10): « Dashboard shows the funnel, not
// one %. chats → photos sent → preview approved → confirmed. Each step with its
// count and %. Mark the date the rule changed on the chart. »
//
// % = of the people who WROTE in the window. Archived orders (no photos, no
// money) are never counted; the people behind them stay in « chats ».
import { useQuery } from '@tanstack/react-query';
import { marketingApi, type WhatsappFunnel, type WhatsappFunnelStepKey } from '@/lib/api';
import { ADM_COLORS as COLORS, withAlpha } from '@/lib/brand';
import { useT } from '@/lib/i18n';

const HEADING = "'Fraunces', 'Cormorant Garamond', Georgia, serif";
const cardStyle = {
  background: COLORS.bgCard,
  borderColor: COLORS.borderSubtle,
  boxShadow: '0 1px 3px rgba(46,36,24,0.04), 0 8px 24px rgba(46,36,24,0.05)',
} as const;

const STEP_LABEL: Record<WhatsappFunnelStepKey, string> = {
  chats: 'Ont écrit sur WhatsApp',
  photos: 'Ont envoyé leurs photos',
  approved: 'Ont approuvé l’aperçu 3D',
  confirmed: 'Commande confirmée',
};
/** One ink per step, light → dark: the funnel narrows and darkens. */
const STEP_ALPHA: Record<WhatsappFunnelStepKey, number> = { chats: 0.22, photos: 0.45, approved: 0.7, confirmed: 1 };
const STEPS: WhatsappFunnelStepKey[] = ['chats', 'photos', 'approved', 'confirmed'];

const dayLabel = (iso: string) => {
  const d = new Date(Date.parse(iso));
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

function WeeksChart({ f }: { f: WhatsappFunnel }) {
  const { t } = useT();
  if (!f.weeks.length) return null;
  const COL = 44; const GAP = 10; const H = 120; const TOP = 14; const BOTTOM = 18;
  const width = f.weeks.length * (COL + GAP) + GAP;
  const max = Math.max(1, ...f.weeks.map((w) => w.chats));
  const bar = (Math.floor((COL - 6) / 4));
  // The rule's date, placed inside its week by day.
  let ruleX: number | null = null;
  if (f.ruleOnAt) {
    const r = Date.parse(f.ruleOnAt);
    const idx = f.weeks.findIndex((w) => {
      const s = Date.parse(`${w.week}T00:00:00Z`);
      return r >= s && r < s + 7 * 86_400_000;
    });
    const first = Date.parse(`${f.weeks[0].week}T00:00:00Z`);
    if (idx >= 0) {
      const frac = (r - Date.parse(`${f.weeks[idx].week}T00:00:00Z`)) / (7 * 86_400_000);
      ruleX = GAP + idx * (COL + GAP) + frac * COL;
    } else if (r < first) ruleX = GAP / 2;
  }
  return (
    <div className="mt-4 overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${TOP + H + BOTTOM}`} width={width} height={TOP + H + BOTTOM} role="img"
        aria-label={t('Entonnoir WhatsApp par semaine')} style={{ maxWidth: '100%', minWidth: Math.min(width, 280) }}>
        {f.weeks.map((w, i) => {
          const x0 = GAP + i * (COL + GAP);
          return (
            <g key={w.week}>
              {STEPS.map((k, j) => {
                const v = w[k];
                const h = Math.round((v / max) * H);
                return (
                  <rect key={k} x={x0 + 3 + j * bar} y={TOP + H - h} width={bar - 1} height={Math.max(h, v > 0 ? 1 : 0)}
                    rx={1.5} fill={withAlpha(COLORS.accentPrimary, STEP_ALPHA[k])}>
                    <title>{`${t('Semaine du {d}', { d: dayLabel(w.week) })} — ${t(STEP_LABEL[k])} : ${v}`}</title>
                  </rect>
                );
              })}
              <text x={x0 + COL / 2} y={TOP + H + 13} textAnchor="middle" fontSize="9" fill={COLORS.textTertiary}>{dayLabel(w.week)}</text>
            </g>
          );
        })}
        {ruleX != null && (
          <g>
            <line x1={ruleX} x2={ruleX} y1={TOP - 4} y2={TOP + H} stroke={COLORS.error} strokeWidth={1.25} strokeDasharray="3 3" />
            <text x={ruleX + 3} y={TOP + 2} fontSize="9" fill={COLORS.error}>{t('règle photos/argent')}</text>
          </g>
        )}
      </svg>
    </div>
  );
}

export function WhatsappFunnelCard({ from, to }: { from?: string; to?: string }) {
  const { t } = useT();
  const q = useQuery({
    queryKey: ['whatsapp-funnel', from, to],
    queryFn: () => marketingApi.whatsappFunnel({ from, to }),
    staleTime: 60_000,
  });
  const f = q.data?.funnel;
  return (
    <div className="rounded-xl p-4 border adm-rise mb-3" style={cardStyle}>
      <p className="text-[11px] font-medium uppercase tracking-wider mb-3" style={{ color: COLORS.textTertiary }}>
        {t('Entonnoir WhatsApp')}
      </p>
      {q.isLoading && <p className="text-sm py-4" style={{ color: COLORS.textTertiary }}>{t('Chargement…')}</p>}
      {q.isError && <p className="text-sm py-4" style={{ color: COLORS.error }}>{(q.error as Error).message}</p>}
      {f && (
        <>
          <ol className="space-y-2.5">
            {f.steps.map((s) => (
              <li key={s.key}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[13px]" style={{ color: COLORS.textSecondary }}>{t(STEP_LABEL[s.key])}</span>
                  <span className="shrink-0 tabular-nums">
                    <span className="text-[18px] leading-none" style={{ color: COLORS.textPrimary, fontFamily: HEADING, fontWeight: 500 }}>{s.count}</span>
                    {s.key !== 'chats' && (
                      <span className="text-[12px] ml-1.5" style={{ color: COLORS.textTertiary }}>{s.pct == null ? '—' : `${s.pct}%`}</span>
                    )}
                  </span>
                </div>
                <div className="h-2 rounded-full mt-1" style={{ background: withAlpha(COLORS.accentPrimary, 0.08) }}>
                  <div className="h-2 rounded-full" style={{ width: `${s.pct ?? 0}%`, background: withAlpha(COLORS.accentPrimary, STEP_ALPHA[s.key]) }} />
                </div>
              </li>
            ))}
          </ol>
          <WeeksChart f={f} />
          <p className="text-[10px] mt-2" style={{ color: COLORS.textTertiary }}>
            {t('% = sur les personnes qui ont écrit dans la période (par semaine de leur premier message).')}{' '}
            {t('{n} commandes archivées (ni photos ni argent) ne comptent pas — ces personnes restent dans « ont écrit ».', { n: f.archivedOrders })}
          </p>
          <p className="text-[10px] mt-1" style={{ color: f.ruleOnAt ? COLORS.error : COLORS.textTertiary }}>
            {f.ruleOnAt
              ? t('Règle « commande = photos ou argent » activée le {d}.', { d: new Date(f.ruleOnAt).toLocaleDateString('fr-FR') })
              : t('Règle « commande = photos ou argent » : pas encore activée.')}
          </p>
        </>
      )}
    </div>
  );
}
